document.addEventListener("alpine:init", () => {

    // Generates datepicker locale using the browser's Intl API.
    // Works for any language without hardcoding month/day names.
    function buildDatepickerLocale(lang) {
        const bcp47 = lang === 'sv' ? 'sv-SE' : lang;
        const cap   = s => s.replace(/^\w/, c => c.toUpperCase());
        // Jan 2 2000 is a Sunday -- the array starts with Sunday (index 0).
        return {
            monthNames:  Array.from({ length: 12 }, (_, i) => cap(new Date(2000, i, 1).toLocaleString(bcp47, { month: 'long' }))),
            dayNamesMin: Array.from({ length: 7  }, (_, i) => cap(new Date(2000, 0, 2 + i).toLocaleString(bcp47, { weekday: 'narrow' }))),
        };
    }

    // Singular/plural labels for guestSummary.
    const labels = {
        sv: { roomSg: 'rum',         roomPl: 'rum',           guestSg: 'gäst',     guestPl: 'gäster'    },
        en: { roomSg: 'room',        roomPl: 'rooms',         guestSg: 'guest',    guestPl: 'guests'    },
        de: { roomSg: 'Zimmer',      roomPl: 'Zimmer',        guestSg: 'Gast',     guestPl: 'Gäste'     },
        fr: { roomSg: 'chambre',     roomPl: 'chambres',      guestSg: 'voyageur', guestPl: 'voyageurs' },
        es: { roomSg: 'habitación',  roomPl: 'habitaciones',  guestSg: 'huésped',  guestPl: 'huéspedes' },
        zh: { roomSg: '间客房',       roomPl: '间客房',          guestSg: '位客人',   guestPl: '位客人'     },
    };

    function sameDay(a, b) {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    /**
     * Small calendar popup, positioned near `anchorEl`. Replaces the old
     * jQuery UI Datepicker widget -- built fresh on every open() and torn
     * down on close(), no persistent DOM between opens. Visual design mirrors
     * gravity-forms.css's .gform-datepicker-calendar (Gravity Forms' own
     * vanilla calendar), see assets/src/css/components/datepicker.css.
     */
    function openCalendarPopup({ anchorEl, locale, initialDate, minDate, onSelect, onClose }) {
        let viewYear = initialDate.getFullYear();
        let viewMonth = initialDate.getMonth();

        const popup = document.createElement('div');
        popup.className = 'tb-datepicker';
        popup.setAttribute('role', 'dialog');
        popup.setAttribute('aria-label', 'Kalender');

        const header = document.createElement('div');
        header.className = 'tb-datepicker-header';

        const monthSelect = document.createElement('select');
        monthSelect.className = 'tb-datepicker-select';
        monthSelect.setAttribute('aria-label', 'Månad');
        locale.monthNames.forEach((name, i) => {
            const opt = document.createElement('option');
            opt.value = String(i);
            opt.textContent = name;
            monthSelect.appendChild(opt);
        });

        const yearSelect = document.createElement('select');
        yearSelect.className = 'tb-datepicker-select';
        yearSelect.setAttribute('aria-label', 'År');
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y <= currentYear + 2; y++) {
            const opt = document.createElement('option');
            opt.value = String(y);
            opt.textContent = String(y);
            yearSelect.appendChild(opt);
        }

        header.append(monthSelect, yearSelect);

        const table = document.createElement('table');
        table.className = 'tb-datepicker-calendar';

        const thead = document.createElement('thead');
        const headRow = document.createElement('tr');
        const weekOrder = [1, 2, 3, 4, 5, 6, 0]; // Monday-first
        weekOrder.forEach((i) => {
            const th = document.createElement('th');
            th.className = 'week';
            th.textContent = locale.dayNamesMin[i];
            headRow.appendChild(th);
        });
        thead.appendChild(headRow);
        table.appendChild(thead);

        const tbody = document.createElement('tbody');
        table.appendChild(tbody);

        popup.append(header, table);
        document.body.appendChild(popup);

        function renderMonth() {
            monthSelect.value = String(viewMonth);
            yearSelect.value = String(viewYear);
            tbody.innerHTML = '';

            const startOffset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday-aligned
            const totalDays = new Date(viewYear, viewMonth + 1, 0).getDate();
            const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
            const cellCount = Math.ceil((startOffset + totalDays) / 7) * 7;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            for (let row = 0; row < cellCount / 7; row++) {
                const tr = document.createElement('tr');
                for (let col = 0; col < 7; col++) {
                    const n = row * 7 + col;
                    let day, cellDate, adjacent;
                    if (n < startOffset) {
                        day = prevMonthDays - startOffset + 1 + n;
                        cellDate = new Date(viewYear, viewMonth - 1, day);
                        adjacent = true;
                    } else if (n < startOffset + totalDays) {
                        day = n - startOffset + 1;
                        cellDate = new Date(viewYear, viewMonth, day);
                        adjacent = false;
                    } else {
                        day = n - startOffset - totalDays + 1;
                        cellDate = new Date(viewYear, viewMonth + 1, day);
                        adjacent = true;
                    }

                    const td = document.createElement('td');
                    td.className = 'day' + (adjacent ? ' adjacent' : '');
                    td.textContent = String(day);
                    if (sameDay(cellDate, today)) td.classList.add('today');
                    if (sameDay(cellDate, initialDate)) td.classList.add('selected');

                    if (cellDate < minDate) {
                        td.setAttribute('aria-disabled', 'true');
                    } else {
                        td.setAttribute('tabindex', '0');
                        td.setAttribute('role', 'button');
                        td.addEventListener('click', () => { onSelect(cellDate); close(); });
                        td.addEventListener('keydown', (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelect(cellDate);
                                close();
                            }
                        });
                    }

                    tr.appendChild(td);
                }
                tbody.appendChild(tr);
            }
        }

        function position() {
            const rect = anchorEl.getBoundingClientRect();
            const popRect = popup.getBoundingClientRect();
            const top  = (rect.bottom + popRect.height > window.innerHeight) ? rect.top - popRect.height : rect.bottom;
            const left = (rect.left + popRect.width > window.innerWidth) ? rect.right - popRect.width : rect.left;
            popup.style.top  = Math.max(0, top) + 'px';
            popup.style.left = Math.max(0, left) + 'px';
        }

        function onDocPointerDown(e) {
            if (!popup.contains(e.target) && !anchorEl.contains(e.target)) close();
        }
        function onKeydown(e) {
            if (e.key === 'Escape') close();
        }

        let closed = false;
        function close() {
            if (closed) return;
            closed = true;
            document.removeEventListener('pointerdown', onDocPointerDown, true);
            document.removeEventListener('keydown', onKeydown);
            popup.remove();
            onClose?.();
        }

        monthSelect.addEventListener('change', () => { viewMonth = +monthSelect.value; renderMonth(); position(); });
        yearSelect.addEventListener('change', () => { viewYear = +yearSelect.value; renderMonth(); position(); });

        renderMonth();
        position();

        document.addEventListener('pointerdown', onDocPointerDown, true);
        document.addEventListener('keydown', onKeydown);

        return { close };
    }

    Alpine.data("travelClick", () => ({
        rooms: 1,
        adults: 2,
        children: 0,
        guestsOpen: false,
        baseUrl: '',
        lang: 'sv',
        datein: '',
        dateout: '',

        activePopup: null,

        init() {
            this.baseUrl = this.$el.dataset.baseUrl ?? '';
            this.lang    = this.$el.dataset.lang    ?? 'sv';

            // Keep lang in sync if Weglot switches language client-side (without reload).
            if (window.Weglot) {
                Weglot.on('languageChanged', (newLang) => { this.lang = newLang; });
            }

            const today    = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            this.datein  = this.formatDate(today);
            this.dateout = this.formatDate(tomorrow);
        },

        formatDate(date) {
            const mm   = String(date.getMonth() + 1).padStart(2, '0');
            const dd   = String(date.getDate()).padStart(2, '0');
            const yyyy = date.getFullYear();
            return `${mm}/${dd}/${yyyy}`;
        },

        parseDate(dateStr) {
            const [mm, dd, yyyy] = dateStr.split('/');
            return new Date(+yyyy, +mm - 1, +dd);
        },

        parseDisplay(dateStr) {
            if (!dateStr) return { day: '', month: '' };
            const date  = this.parseDate(dateStr);
            const bcp47 = this.lang === 'sv' ? 'sv-SE' : this.lang;
            return {
                day:   String(date.getDate()),
                month: date.toLocaleString(bcp47, { month: 'long' }).replace(/^\w/, c => c.toUpperCase()),
            };
        },

        get checkinDisplay()  { return this.parseDisplay(this.datein);  },
        get checkoutDisplay() { return this.parseDisplay(this.dateout); },

        openCheckin() {
            this.activePopup?.close();
            const self = this;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            this.activePopup = openCalendarPopup({
                anchorEl: this.$refs.checkin.parentElement,
                locale: buildDatepickerLocale(this.lang),
                initialDate: this.parseDate(this.datein),
                minDate: today,
                onSelect(date) {
                    self.datein = self.formatDate(date);
                    const nextMin = new Date(date);
                    nextMin.setDate(nextMin.getDate() + 1);
                    if (self.parseDate(self.dateout) <= date) {
                        self.dateout = self.formatDate(nextMin);
                    }
                },
                onClose: () => { self.activePopup = null; },
            });
        },

        openCheckout() {
            this.activePopup?.close();
            const self = this;
            const minDate = this.parseDate(this.datein);
            minDate.setDate(minDate.getDate() + 1);
            this.activePopup = openCalendarPopup({
                anchorEl: this.$refs.checkout.parentElement,
                locale: buildDatepickerLocale(this.lang),
                initialDate: this.parseDate(this.dateout),
                minDate,
                onSelect(date) {
                    self.dateout = self.formatDate(date);
                },
                onClose: () => { self.activePopup = null; },
            });
        },

        get bookingUrl() {
            const params = new URLSearchParams({
                languageid: this.lang === 'sv' ? 16 : 1,
                datein:     this.datein,
                dateout:    this.dateout,
                adults:     this.adults,
                children:   this.children,
                rooms:      this.rooms,
            });
            return this.baseUrl + params.toString() + '#/accommodation/room';
        },

        get guestSummary() {
            const tr         = labels[this.lang] ?? labels.sv;
            const total      = this.adults + this.children;
            const roomLabel  = this.rooms === 1 ? tr.roomSg : tr.roomPl;
            const guestLabel = total      === 1 ? tr.guestSg : tr.guestPl;
            return `${this.rooms} ${roomLabel}, ${total} ${guestLabel}`;
        },

        atMin(field, min = 0) { return this[field] <= min; },
        atMax(field, max = 10) { return this[field] >= max; },

        increment(field) {
            if (!this.atMax(field)) this[field]++;
        },

        decrement(field, min = 0) {
            if (!this.atMin(field, min)) this[field]--;
        },
    }));
});

//# sourceMappingURL=travelclick.js.map
